import React, { useState, useEffect } from 'react';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import { Panel } from '../components/ui/Panel';
import { EmptyState } from '../components/ui/EmptyState';
import { FadeIn } from '../components/ui/FadeIn';

interface Props {
  body: string;
  isManager: boolean;
  onSave: (next: string) => Promise<void>;
}

/**
 * Top-of-page banner for "overall things to make note of" — the freeform
 * paragraph at the top of Mannie's POA samples. Manager users can flip
 * into edit mode inline; everyone else sees a `whitespace-pre-wrap` render
 * that preserves line breaks.
 */
const WeekNotesCard: React.FC<Props> = ({ body, isManager, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  const [saving, setSaving] = useState(false);

  // Mirror parent's body into the draft only when not actively editing —
  // otherwise an external save (or refetch) mid-edit clobbers the user's
  // in-progress text.
  useEffect(() => {
    if (!editing) setDraft(body);
  }, [body, editing]);

  const isEmpty = !body || !body.trim();

  const startEdit = () => {
    setDraft(body);
    setEditing(true);
  };
  const cancel = () => {
    setDraft(body);
    setEditing(false);
  };
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title="Overall Notes for the Week"
      titleStyle="section"
      accent="purple"
      lit
      className="h-full"
      actions={isManager && !editing ? (
        <button
          type="button"
          onClick={startEdit}
          className={buttonClass('quiet', 'sm')}
        >
          {isEmpty ? '+ Add notes' : 'Edit →'}
        </button>
      ) : undefined}
    >

      <FadeIn key={editing ? 'edit' : isEmpty ? 'empty' : 'body'} className="flex flex-1 flex-col min-h-0">
      {editing ? (
        <div className="flex-1 flex flex-col min-h-0">
          <TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Anything everyone should know about this week — site visits, fleet updates, OOO coverage…"
            className="flex-1 min-h-[120px] !resize-none"
          />
          <div className="mt-2 flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={buttonClass('filled', 'sm')}
            >
              Save
            </button>
            <button
              type="button"
              onClick={cancel}
              className={buttonClass('quiet', 'sm')}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : isEmpty ? (
        <EmptyState variant="line" dense className="flex-1 items-start">
          {isManager ? 'No notes yet. Click + Add notes to get started.' : 'No notes for this week.'}
        </EmptyState>
      ) : (
        // Scroll within the card if notes exceed the airspace card's height.
        <div className="flex-1 overflow-y-auto text-sm text-on-surface leading-relaxed whitespace-pre-wrap pr-1">
          {body}
        </div>
      )}
      </FadeIn>
    </Panel>
  );
};

export default WeekNotesCard;
